import BoltIcon from "@mui/icons-material/Bolt"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Box, Chip, Collapse, Tooltip, Typography } from "@mui/material"
import { AiMessageSteps } from "__generated__/graphql"
import { useMemo, useState } from "react"

import { ROLE_LABELS } from "./aiStepsDrawer.utils"
import { readableStepText } from "./stepCodes.labels"
import V2AgentLoop, { ACTION_TONE } from "./V2AgentLoop"
import {
  ACTION_LABELS,
  CONFIDENCE_LABELS,
  CONVERSATION_STATE_LABELS,
  ROLE_REASON_LABELS,
  SURFACE_LABELS
} from "./v2AgentLoop.labels"
import { buildV2AgentLoop } from "./v2AgentLoop.utils"
import { buildV2RunOverview } from "./v2RunOverview.utils"

const DELIVERY_LABELS: Record<string, string> = {
  SEND: "Gönderildi",
  SEND_AND_HANDOFF: "Gönderildi + devredildi",
  HANDOFF_SILENT: "Sessiz devir",
  SPAM: "Spam işaretlendi",
  SPAM_NOT_CONFIGURED: "Spam yazılmamış — işaretlenmedi",
  NO_REPLY: "Cevap yok",
  SINGLE_REPLY_WITHHELD: "Tek yanıt kuralı — tutuldu",
  EMPTY_REPLY_WITHHELD: "Boş cevap — tutuldu"
}

const LANE_LABELS: Record<string, string> = {
  MARKETPLACE: "yalnızca bu ilanın araçları",
  STOREFRONT: "tüm mağaza araçları",
  NONE: "kapalı"
}

function Fact({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <Box
      sx={{ display: "flex", gap: 0.75, alignItems: "baseline", minWidth: 0 }}
    >
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", flexShrink: 0, fontSize: 10.5 }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {children}
      </Box>
    </Box>
  )
}

function Pill({
  label,
  fg,
  bg,
  title
}: {
  label: string
  fg?: string
  bg?: string
  title?: string
}) {
  const pill = (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 18,
        fontSize: 10.5,
        fontWeight: 600,
        maxWidth: 220,
        ...(fg && { color: fg }),
        ...(bg && { bgcolor: bg })
      }}
    />
  )
  return title ? (
    <Tooltip arrow title={title}>
      {pill}
    </Tooltip>
  ) : (
    pill
  )
}

/**
 * A v2 turn at a glance, above the raw step list: which role answered with what on the table,
 * what the agent did in each round, what it decided, and what the host did with that decision.
 *
 * The step list underneath is 25 rows for one turn and holds the same facts inside four
 * different payloads — true, but not readable at the speed someone triaging a run needs.
 */
export default function V2RunOverview({
  steps,
  onJumpToStep
}: {
  steps: AiMessageSteps[] | undefined
  onJumpToStep?: (stepId: string) => void
}) {
  const overview = useMemo(() => buildV2RunOverview(steps), [steps])
  const loop = useMemo(() => buildV2AgentLoop(steps), [steps])
  const [open, setOpen] = useState(true)

  if (!overview) return null

  const { offered, output, delivery } = overview
  const actionTone = output?.action ? ACTION_TONE[output.action] : undefined
  const roleLabel = overview.role
    ? (ROLE_LABELS[overview.role] ?? overview.role)
    : null
  const replyMeta = output
    ? [
        output.confidence
          ? `emin: ${CONFIDENCE_LABELS[output.confidence] ?? output.confidence}`
          : "",
        output.language ?? "",
        output.conversationState
          ? (CONVERSATION_STATE_LABELS[output.conversationState] ??
            output.conversationState)
          : "",
        output.answeringRole &&
        output.answeringRole !== "UNDECIDED" &&
        output.answeringRole !== overview.role
          ? `ajanın seçtiği rol: ${ROLE_LABELS[output.answeringRole] ?? output.answeringRole}`
          : ""
      ].filter(Boolean)
    : []
  const deliveryExtras = delivery
    ? [
        delivery.sent ? "" : "müşteriye mesaj gitmedi",
        delivery.dm ? "özelden (DM) de yazıldı" : "",
        delivery.productCards > 0
          ? `${delivery.productCards} ürün kartı gönderildi`
          : ""
      ].filter(Boolean)
    : []

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#fbfbfa" }}>
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 0.75,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" }
        }}
      >
        <BoltIcon sx={{ fontSize: 15, color: "#5f43db" }} />
        <Typography
          variant="caption"
          sx={{
            textTransform: "uppercase",
            fontSize: 10,
            letterSpacing: 0.6,
            fontWeight: 700,
            color: "text.secondary"
          }}
        >
          v2 turu
        </Typography>
        {roleLabel ? (
          <Tooltip
            arrow
            title={[
              overview.roleReason
                ? (ROLE_REASON_LABELS[overview.roleReason] ??
                  overview.roleReason)
                : "",
              overview.surface
                ? `gelen: ${SURFACE_LABELS[overview.surface] ?? overview.surface}`
                : ""
            ]
              .filter(Boolean)
              .join(" · ")}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {roleLabel}
            </Typography>
          </Tooltip>
        ) : null}
        <Typography variant="caption" noWrap sx={{ color: "text.secondary" }}>
          {overview.toolCalls} araç
          {overview.failures > 0 ? (
            <Box component="span" sx={{ color: "error.main", fontWeight: 600 }}>
              {` · ${overview.failures} hata`}
            </Box>
          ) : null}
        </Typography>
        <Box
          sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}
        >
          {output?.action ? (
            <Pill
              label={ACTION_LABELS[output.action] ?? output.action}
              fg={actionTone}
              bg="rgba(0,0,0,0.04)"
            />
          ) : null}
          {delivery?.code ? (
            <Pill
              label={DELIVERY_LABELS[delivery.code] ?? delivery.code}
              title={readableStepText("DELIVERY_GATE", delivery.code)}
              bg="rgba(0,0,0,0.04)"
            />
          ) : null}
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              color: "text.secondary",
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform .15s"
            }}
          />
        </Box>
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            px: 2,
            pb: 1.25,
            display: "flex",
            flexDirection: "column",
            gap: 0.75
          }}
        >
          <Fact label="Açık kaynaklar">
            {offered.knowledge ? <Pill label="Bilgi tabanı" /> : null}
            {offered.guidance > 0 ? (
              <Pill label={`${offered.guidance} rehberlik kuralı`} />
            ) : null}
            {offered.activeSkill ? (
              <Pill label={`Devam eden skill: ${offered.activeSkill}`} />
            ) : offered.skills.length > 0 ? (
              <Pill
                label={`${offered.skills.length} skill seçeneği`}
                title={offered.skills.join(", ")}
              />
            ) : null}
            {offered.actions.length > 0 ? (
              <Pill
                label={`${offered.actions.length} kanal action'ı`}
                title={offered.actions.join(", ")}
              />
            ) : null}
            {offered.commerceLane ? (
              <Pill
                label={`Mağaza: ${LANE_LABELS[offered.commerceLane] ?? offered.commerceLane}`}
                title={offered.commerceTools.join(", ")}
              />
            ) : null}
            {!offered.knowledge &&
            offered.guidance === 0 &&
            offered.skills.length === 0 &&
            offered.actions.length === 0 &&
            !offered.commerceLane ? (
              <Typography variant="caption" sx={{ color: "text.disabled" }}>
                hiçbir kaynak açık değildi
              </Typography>
            ) : null}
          </Fact>

          {loop ? (
            <V2AgentLoop
              loop={loop}
              replyMeta={replyMeta}
              onJumpToStep={onJumpToStep}
            />
          ) : null}

          {output?.handoffReason ? (
            <Fact label="İnsana devir gerekçesi">
              <Typography variant="caption">{output.handoffReason}</Typography>
            </Fact>
          ) : null}
          {output?.knowledgeGap ? (
            <Fact label="Bilgi tabanında eksik">
              <Typography variant="caption">{output.knowledgeGap}</Typography>
            </Fact>
          ) : null}
          {deliveryExtras.length > 0 ? (
            <Fact label="Sonuç">
              {deliveryExtras.map((extra) => (
                <Pill key={extra} label={extra} />
              ))}
            </Fact>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  )
}
